/* ============================================================================
   SVNTY FOOTER — the four behaviours the sibling's footer carries, ported as is.
   SVNTY, night build lane H, 2026-09-19, dev1.0.
   Loaded (defer) by sections/footer.liquid only.

   SOURCES
     1. THE ACCORDION TWEEN — the sibling assets/sib-accordion.js:1-108, scoped here to
        `details[data-svnty-ft-accordion]`. A native <details> snaps shut with no motion and
        pops open in one frame; every footer group opens and closes on a height tween (Web
        Animations API) while the panel fades, and the chevron turns the moment the tap lands.
        Interrupt-safe: tapping mid-tween reverses from the current height.
     2. THE STATE MACHINE — sib-footer.liquid:996-1012. Collapsed on a phone, forced open
        from 750px up (where the summary is inert), and the phone's own choice is remembered
        across a rotation.
     3. THE TYPEWRITER — sib-footer.liquid:1014-1080. A faux placeholder types and erases the
        section's terms; it idles while off screen or the tab is hidden, so the tightest timer
        chain on the page costs nothing at the bottom of every template.
     4. THE STAMP — sib-footer.liquid:1082-1120 + its comments at :66-84 and :117-129.
        Shopify does NOT hand the id passed to {% form %} back as `form.id`, and
        `form.posted_successfully?` / `form.errors` are true for EVERY {% form 'customer' %} on
        the page after ANY customer post (the welcome pop-up and the notify sheet included). So
        the error note is rendered HIDDEN and only revealed here, behind three walls: a stamp
        written one statement before the browser posts, an arrival that is a navigate or a
        reload (never the Back button re-parsing a cached page), and a flag spent on first read.
     5. THE REVEAL — sib-footer.liquid:1122-1132.
   No dependency, no build step, ES5 only (the theme ships no bundler).
   ============================================================================ */
(function () {
  'use strict';

  var PHONE = '(max-width: 749px)';
  var DURATION_OPEN = 380;
  var DURATION_CLOSE = 300;
  var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  var canAnimate = 'animate' in Element.prototype;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var running = new WeakMap(); /* details -> { height, panel, state } */

  /* ------------------------------------------------------------------ 1. the tween */
  function boxExtra(el) {
    var cs = getComputedStyle(el);
    return (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0) +
      (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
  }

  function cancelRun(details) {
    var run = running.get(details);
    if (!run) return;
    if (run.height) run.height.cancel();
    if (run.panel) run.panel.cancel();
    running['delete'](details);
  }

  function settle(details) {
    details.style.height = '';
    details.style.overflow = '';
    details.classList.remove('is-closing', 'is-animating');
    running['delete'](details);
  }

  function openPanel(details, panel) {
    var start = details.offsetHeight;
    cancelRun(details);
    details.classList.remove('is-closing');
    details.classList.add('is-animating');
    details.style.overflow = 'hidden';
    details.style.height = '';
    details.open = true;
    var end = details.offsetHeight;
    var height = details.animate({ height: [start + 'px', end + 'px'] }, { duration: DURATION_OPEN, easing: EASE });
    var fade = panel
      ? panel.animate(
          { opacity: [0, 1], transform: ['translateY(-6px)', 'translateY(0)'] },
          { duration: DURATION_OPEN - 60, delay: 60, easing: EASE, fill: 'backwards' }
        )
      : null;
    running.set(details, { height: height, panel: fade, state: 'opening' });
    height.onfinish = function () { settle(details); };
  }

  function closePanel(details, summary, panel) {
    var start = details.offsetHeight;
    cancelRun(details);
    details.classList.add('is-closing', 'is-animating');
    details.style.overflow = 'hidden';
    var end = summary.offsetHeight + boxExtra(details);
    var height = details.animate({ height: [start + 'px', end + 'px'] }, { duration: DURATION_CLOSE, easing: EASE });
    var fade = panel
      ? panel.animate(
          { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-4px)'] },
          { duration: DURATION_CLOSE * 0.6, easing: 'ease-out', fill: 'forwards' }
        )
      : null;
    running.set(details, { height: height, panel: fade, state: 'closing' });
    height.onfinish = function () {
      if (fade) fade.cancel();
      details.open = false;
      settle(details);
    };
  }

  if (canAnimate) {
    document.addEventListener('click', function (e) {
      if (reduce.matches) return;
      var summary = e.target && e.target.closest ? e.target.closest('summary') : null;
      if (!summary) return;
      var details = summary.parentElement;
      if (!details || details.tagName !== 'DETAILS' || !details.hasAttribute('data-svnty-ft-accordion')) return;
      /* ≥750px the summary is inert in CSS; nothing to tween there */
      if (!window.matchMedia(PHONE).matches) return;
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.target.closest('a, button, input')) return;
      e.preventDefault();
      var panel = summary.nextElementSibling;
      var run = running.get(details);
      var isOpen = run ? run.state === 'opening' : details.open;
      if (isOpen) closePanel(details, summary, panel);
      else openPanel(details, panel);
    }, true);
  }

  /* ------------------------------------------------------------------ per-footer wiring */
  function init(root) {
    if (root.dataset.ftInit) return;
    root.dataset.ftInit = '1';

    /* --- 2. collapsed on a phone, open from 750px up, the phone's choice remembered --- */
    var mq = window.matchMedia(PHONE);
    var cols = root.querySelectorAll('details[data-svnty-ft-accordion]');
    function applyState() {
      Array.prototype.forEach.call(cols, function (d) {
        if (mq.matches) {
          d.open = d.dataset.ftUser ? d.dataset.ftUser === 'open' : d.hasAttribute('data-ft-open-on-phone');
        } else {
          d.open = true;
        }
        /* D5-2 (D-10): from 750 px the group heading is a heading, not a control: out of the Tab
           order and never a toggle (Enter and Space fire click, the guard below eats it). */
        var sum = d.querySelector('summary');
        if (sum) {
          if (mq.matches) sum.removeAttribute('tabindex');
          else sum.setAttribute('tabindex', '-1');
        }
      });
    }
    Array.prototype.forEach.call(cols, function (d) {
      d.addEventListener('toggle', function () {
        if (mq.matches) d.dataset.ftUser = d.open ? 'open' : 'closed';
        else if (!d.open) d.open = true;
      });
      var sum = d.querySelector('summary');
      if (sum) sum.addEventListener('click', function (e) {
        if (!mq.matches) e.preventDefault();
      });
    });
    if (mq.addEventListener) mq.addEventListener('change', applyState);
    else if (mq.addListener) mq.addListener(applyState);
    applyState();

    /* --- 3. the typewriter faux placeholder --- */
    var field = root.querySelector('[data-ft-field]');
    var input = field && field.querySelector('[data-ft-email]');
    var termEl = field && field.querySelector('[data-ft-term]');
    if (field && input && termEl) {
      var terms = (field.getAttribute('data-ft-terms') || '')
        .split(',')
        .map(function (t) { return t.trim(); })
        .filter(Boolean);
      if (terms.length) {
        field.classList.add('is-typewriter');
        input.setAttribute('placeholder', '');

        var syncActive = function () {
          var active = document.activeElement === input || input.value.length > 0;
          field.classList.toggle('is-active', active);
        };
        input.addEventListener('focus', syncActive);
        input.addEventListener('blur', syncActive);
        input.addEventListener('input', syncActive);
        syncActive();

        if (reduce.matches) {
          var ri = 0;
          termEl.textContent = terms[0];
          if (terms.length > 1) {
            window.setInterval(function () {
              ri = (ri + 1) % terms.length;
              termEl.textContent = terms[ri];
            }, 2600);
          }
        } else {
          var TYPE_MIN = 55, TYPE_MAX = 95, ERASE = 32, HOLD = 1400, GAP = 400, idx = 0;
          /* the footer sits at the bottom of every template: idle-poll while off screen */
          var onScreen = true;
          try {
            new IntersectionObserver(function (es) {
              for (var k = 0; k < es.length; k += 1) onScreen = es[k].isIntersecting;
            }).observe(termEl);
          } catch (e) {}
          var typeDelay = function () { return TYPE_MIN + Math.floor(Math.random() * (TYPE_MAX - TYPE_MIN + 1)); };
          var typeIn = function (full, n, done) {
            if (!onScreen || document.hidden) { window.setTimeout(function () { typeIn(full, n, done); }, 800); return; }
            termEl.textContent = full.slice(0, n);
            if (n >= full.length) { window.setTimeout(done, HOLD); return; }
            window.setTimeout(function () { typeIn(full, n + 1, done); }, typeDelay());
          };
          var eraseOut = function (full, n, done) {
            if (!onScreen || document.hidden) { window.setTimeout(function () { eraseOut(full, n, done); }, 800); return; }
            termEl.textContent = full.slice(0, n);
            if (n <= 0) { window.setTimeout(done, GAP); return; }
            window.setTimeout(function () { eraseOut(full, n - 1, done); }, ERASE);
          };
          var cycle = function () {
            var full = terms[idx];
            typeIn(full, 0, function () {
              eraseOut(full, full.length, function () {
                idx = (idx + 1) % terms.length;
                cycle();
              });
            });
          };
          cycle();
        }
      }
    }

    /* --- 4. the stamp: the loading state on the way out, the error on the way back --- */
    var form = root.querySelector('form.svnty-ft__form');
    if (form) {
      /* D5-2 (D-04): Shopify's hCaptcha intercepts the submit (preventDefault, then its own
         form.submit() after the challenge). The busy state is set only when the submit really goes
         out, a tick later, and it can never outlive 10 s without a navigation, nor survive a back
         navigation (pageshow). The stamp is still written, so a post-challenge server error finds
         its note; an empty note is never revealed (D-05). */
      var sbtn = form.querySelector('[data-ft-submit]');
      var slabel = sbtn && sbtn.querySelector('[data-ft-submit-label]');
      var idle = slabel ? slabel.textContent : '';
      var busyTimer = 0;
      var unbusy = function () {
        window.clearTimeout(busyTimer);
        if (!sbtn) return;
        sbtn.removeAttribute('aria-busy');
        if (slabel) slabel.textContent = idle;
      };
      form.addEventListener('submit', function (e) {
        try { sessionStorage.setItem('svnty:footer:native', String(Date.now())); } catch (x) {}
        if (!sbtn) return;
        window.setTimeout(function () {
          if (e.defaultPrevented) return;
          sbtn.setAttribute('aria-busy', 'true');
          var busy = sbtn.getAttribute('data-ft-busy-label');
          if (slabel && busy) slabel.textContent = busy;
          window.clearTimeout(busyTimer);
          busyTimer = window.setTimeout(unbusy, 10000);
        }, 0);
      });
      window.addEventListener('pageshow', unbusy);
    }

    var err = root.querySelector('[data-ft-native-error]');
    if (err) {
      var take = function () {
        try {
          var nav = (performance.getEntriesByType && performance.getEntriesByType('navigation')) || [];
          var type = nav.length ? nav[0].type : 'navigate';
          if (type !== 'navigate' && type !== 'reload') return false;
          var raw = sessionStorage.getItem('svnty:footer:native');
          sessionStorage.removeItem('svnty:footer:native');
          var t = parseInt(raw || '', 10);
          return !!t && Date.now() - t >= 0 && Date.now() - t < 30000;
        } catch (e) { return false; }
      };
      if (take() && err.textContent.trim()) {
        err.hidden = false;
        /* R3-43: revealing the note is also when the INPUT has to point at it. A red line with no
           programmatic association is a line a screen reader never ties to the field. */
        var field = root.querySelector('[data-ft-email]');
        if (field) {
          field.setAttribute('aria-invalid', 'true');
          if (err.id) field.setAttribute('aria-describedby', err.id);
          field.addEventListener('input', function () {
            field.removeAttribute('aria-invalid');
            field.removeAttribute('aria-describedby');
          }, { once: true });
        }
      }
    }

    /* R3b (R3-43): the round above only ever fires AFTER a real server round trip. An invalid or
       empty submit is caught by the browser's own constraint validation first and never reaches
       the server at all — that is exactly the case the refutation measured (native bubble only,
       `aria-invalid`/`aria-describedby` both null). Wire the SAME note to the input's native
       `invalid` event, client-side, before any navigation: `validationMessage` is the browser's
       own localized text, so this needs no new copy and no locale-file edit tonight. */
    if (err && input) {
      input.addEventListener('invalid', function (e) {
        e.preventDefault(); /* suppress the native bubble; our own line carries the message now */
        err.textContent = input.validationMessage || '';
        err.hidden = !err.textContent;
        if (!err.hidden) {
          input.setAttribute('aria-invalid', 'true');
          if (err.id) input.setAttribute('aria-describedby', err.id);
        }
      });
      input.addEventListener('input', function () {
        if (!input.validity.valid) return;
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
        err.hidden = true;
      });
    }

    /* --- 5. the reveal --- */
    if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches && 'IntersectionObserver' in window) {
      root.setAttribute('data-ft-reveal', '');
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { root.classList.add('is-revealed'); io.disconnect(); }
        });
      }, { rootMargin: '0px 0px -10% 0px' });
      io.observe(root);
    }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.svnty-ft'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  /* the theme editor re-renders the section in place */
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    Array.prototype.forEach.call(e.target.querySelectorAll('.svnty-ft'), init);
  });
})();
